// Authentication utility functions
// Supports both:
//   1. Cookie-based auth (HttpOnly cookie managed by browser)
//   2. Token-based auth (JWT stored in localStorage as 'auth_token')
import { BACKEND_URL } from '../config/api';

// Flag to track if cookie-based auth has been verified via API
let cookieAuthVerified = false;

// Flag to track if user has explicitly logged out (prevents auto-reauth on page reload)
let explicitlyLoggedOut = false;

// Check if user explicitly logged out (to prevent auto-reauth)
export const hasExplicitlyLoggedOut = () => {
  return explicitlyLoggedOut || sessionStorage.getItem('explicitLogout') === 'true';
};

// Clear the explicit logout flag (called after successful login)
export const clearExplicitLogout = () => {
  explicitlyLoggedOut = false;
  sessionStorage.removeItem('explicitLogout');
};

// Mark that cookie auth was verified (called after successful API call)
export const setCookieAuthVerified = (verified) => {
  cookieAuthVerified = verified;
  if (verified) {
    localStorage.setItem('cookieAuthActive', 'true');
  } else {
    localStorage.removeItem('cookieAuthActive');
  }
};

// Check if we have verified cookie auth (either in memory or persisted)
export const isCookieAuthActive = () => {
  return cookieAuthVerified || localStorage.getItem('cookieAuthActive') === 'true';
};

// getStoredToken returns the JWT token from localStorage, or a sentinel value when cookie auth is active.
export const getStoredToken = () => {
  // First check for explicit token in localStorage
  const token = localStorage.getItem('auth_token');
  if (token) {
    return token;
  }
  if (isCookieAuthActive()) {
    return 'COOKIE_AUTH';
  }
  return null;
};

// Build authorization headers for API calls
// Returns headers with Bearer token if available, otherwise relies on cookies
export const getAuthHeaders = (additionalHeaders = {}) => {
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Session duration: 7 days in milliseconds
const SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

// Store the login timestamp (call after successful login)
export const setLoginTimestamp = () => {
  localStorage.setItem('auth_login_time', Date.now().toString());
};

// Check if the session has expired (older than 7 days)
export const isSessionExpired = () => {
  const loginTime = localStorage.getItem('auth_login_time');
  if (!loginTime) return false; // No timestamp = legacy session, don't force logout
  return (Date.now() - parseInt(loginTime, 10)) > SESSION_DURATION_MS;
};

export const isAuthenticated = () => {
  const userEmail = localStorage.getItem('userEmail');
  const hasToken = !!localStorage.getItem('auth_token');

  // Check if session has expired (7-day client-side check)
  if (isSessionExpired()) {
    return false;
  }

  // Authenticated if we have a token OR cookie auth is active, plus user data present
  return !!((hasToken || isCookieAuthActive()) && userEmail);
};

export const getUserInfo = () => {
  if (!isAuthenticated()) {
    return null;
  }

  return {
    email: localStorage.getItem('userEmail'),
    name: localStorage.getItem('userName'),
    picture: localStorage.getItem('userPicture'),
    userId: localStorage.getItem('userId'),
  };
};

export const logout = () => {
  // Mark explicit logout to prevent auto-reauth on page reload
  explicitlyLoggedOut = true;
  sessionStorage.setItem('explicitLogout', 'true');
  
  // CRITICAL: Preserve active trial session so users can resume after re-login
  const sessionEndTime = localStorage.getItem('session_end_time');
  const interviewConfig = localStorage.getItem('interview_config');
  let activeTrialConfig = null;
  let activeTrialEndTime = null;

  if (sessionEndTime && interviewConfig) {
    try {
      const config = JSON.parse(interviewConfig);
      const remaining = Math.floor((parseInt(sessionEndTime) - Date.now()) / 1000);
      if (config.isTrial === true && remaining > 0) {
        activeTrialConfig = interviewConfig;
        activeTrialEndTime = sessionEndTime;
      }
    } catch (e) {
      // Invalid config, proceed with normal logout
    }
  }

  // Clear user display data and auth token from localStorage
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  localStorage.removeItem('userPicture');
  localStorage.removeItem('userId');
  localStorage.removeItem('user');
  localStorage.removeItem('uploaded_resume_name');
  localStorage.removeItem('uploaded_resumes');
  localStorage.removeItem('cookieAuthActive');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_login_time');
  
  // Also clear any legacy token keys that may still exist
  localStorage.removeItem('jwt');
  localStorage.removeItem('bk_jwt');
  localStorage.removeItem('googleIdToken');
  
  // Reset in-memory flag
  cookieAuthVerified = false;
  
  // Clear session data (except active trial)
  localStorage.removeItem('resume_content');
  localStorage.removeItem('current_session_id');
  localStorage.removeItem('interview_config');
  localStorage.removeItem('session_end_time');

  // Restore active trial session if it exists
  if (activeTrialConfig && activeTrialEndTime) {
    localStorage.setItem('interview_config', activeTrialConfig);
    localStorage.setItem('session_end_time', activeTrialEndTime);
  }

  // Call backend logout endpoint to clear HttpOnly cookie
  console.log('🚪 [logout] Sending logout request to backend...');
  
  const logoutHeaders = { 'Content-Type': 'application/json' };
  const authToken = localStorage.getItem('auth_token');
  if (authToken) logoutHeaders['Authorization'] = `Bearer ${authToken}`;

  fetch(`${BACKEND_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: logoutHeaders
  })
  .then(res => {
    console.log('✅ [logout] Backend logout response status:', res.status);
    return res.text();
  })
  .then(text => {
    console.log('✅ [logout] Backend logout response:', text);
  })
  .catch((err) => {
    console.error('❌ [logout] Backend logout error:', err);
  });

  console.log('🚪 [logout] ========== LOGOUT COMPLETED ==========');
  window.dispatchEvent(new Event('authChanged'));
};
